import {
    BoxGeometry,
    BufferAttribute,
    BufferGeometry,
    CanvasTexture,
    LinearMipmapLinearFilter,
    RepeatWrapping,
    SRGBColorSpace,
} from 'three';

import { WOOD_TILE_MM } from './stair-board-geometry';

/**
 * Half-width of the grain band: every texel's sRGB luminance stays within ±7 % of the species
 * colour, and the texel mean equals that colour. The owner asked for wood grain "not too bright":
 * ±7 % reads as rings and fibres on the light scene ground, while a dark edge line stays far
 * below the darkest texel of the darkest species, and no texel is pushed towards a saturated or
 * glossy look — the grain scales the colour, it never shifts its hue or saturation.
 */
export const WOOD_GRAIN_AMPLITUDE: number = 0.07;

/** Texels per side of one square tile; one tile covers `WOOD_TILE_MM` of wood on both axes. */
export const WOOD_TEXTURE_SIZE: number = 512;

/** Growth rings across the board per tile: 600 mm / 16 ≈ 37 mm between rings. */
const RINGS_PER_TILE: number = 16;

/** Fibre streaks across the board per tile: 600 mm / 160 ≈ 4 mm apart. */
const FIBRES_PER_TILE: number = 160;

/** Fibre streak length: 600 mm / 6 = 100 mm along the board. */
const FIBRE_RUNS_PER_TILE: number = 6;

/** How far the rings wander off a straight line, in rings. */
const RING_WARP: number = 1.4;

/** Share of rings versus fibres in the grain before it is scaled into the band. */
const RING_WEIGHT: number = 0.65;

/** Box axis the grain runs along: U of the texture follows it. */
export type IGrainAxis = 'x' | 'y' | 'z';

/* eslint-disable no-bitwise -- integer hashing below relies on bitwise ops */
/** FNV-1a: a stable seed per species, so each species has its own pattern. */
export function hashString(text: string): number {
    let hash: number = 2166136261;
    for (let i: number = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
}

function hashGrid(x: number, y: number, seed: number): number {
    let hash: number = seed ^ Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263);
    hash = Math.imul(hash ^ (hash >>> 13), 1274126177);
    hash ^= hash >>> 16;
    return (hash >>> 0) / 0xffffffff;
}
/* eslint-enable no-bitwise */

function wrap(value: number, period: number): number {
    return ((value % period) + period) % period;
}

function smoothstep(fraction: number): number {
    return fraction * fraction * (3 - 2 * fraction);
}

/** Smooth value noise in [0, 1] that repeats every `periodX` × `periodY` cells, so the tile has no seam. */
function periodicNoise(x: number, y: number, periodX: number, periodY: number, seed: number): number {
    const xi: number = Math.floor(x);
    const yi: number = Math.floor(y);
    const tx: number = smoothstep(x - xi);
    const ty: number = smoothstep(y - yi);
    const x0: number = wrap(xi, periodX);
    const x1: number = wrap(xi + 1, periodX);
    const y0: number = wrap(yi, periodY);
    const y1: number = wrap(yi + 1, periodY);
    const top: number = hashGrid(x0, y0, seed) + (hashGrid(x1, y0, seed) - hashGrid(x0, y0, seed)) * tx;
    const bottom: number = hashGrid(x0, y1, seed) + (hashGrid(x1, y1, seed) - hashGrid(x0, y1, seed)) * tx;
    return top + (bottom - top) * ty;
}

/** Raw grain at tile coordinates `u, v` in [0, 1): latewood rings and fibres, both darker than the board. */
function rawGrain(u: number, v: number, seed: number): number {
    // Long waves along the board plus an uneven ring spacing across it.
    const warp: number = periodicNoise(u * 2, v * 2, 2, 2, seed) - 0.5 + 0.5 * (periodicNoise(u, v * 5, 1, 5, seed + 2) - 0.5);
    const ring: number = Math.pow(0.5 + 0.5 * Math.cos(2 * Math.PI * (v * RINGS_PER_TILE + warp * RING_WARP)), 3);
    const fibre: number = periodicNoise(u * FIBRE_RUNS_PER_TILE, v * FIBRES_PER_TILE, FIBRE_RUNS_PER_TILE, FIBRES_PER_TILE, seed + 1) - 0.5;
    return -(RING_WEIGHT * ring + (1 - RING_WEIGHT) * fibre);
}

/**
 * The grain of one tile, `size` × `size`, row by row: each value is in [-1, 1] and the values
 * average to 0. Rings and fibre streaks run along U (the board length), so a texel row is one
 * line of the grain. The tile repeats without a seam on both axes.
 */
export function woodGrainField(size: number, seed: number): Float32Array {
    const field: Float32Array = new Float32Array(size * size);
    let sum: number = 0;
    for (let y: number = 0; y < size; y++) {
        for (let x: number = 0; x < size; x++) {
            const value: number = rawGrain(x / size, y / size, seed);
            field[y * size + x] = value;
            sum += value;
        }
    }
    const mean: number = sum / field.length;
    let peak: number = 0;
    for (let i: number = 0; i < field.length; i++) {
        field[i] -= mean;
        peak = Math.max(peak, Math.abs(field[i]));
    }
    if (peak > 0) {
        for (let i: number = 0; i < field.length; i++) {
            field[i] /= peak;
        }
    }
    return field;
}

/** `#RRGGBB` to its three sRGB channels, 0–255. */
export function parseHexColor(hex: string): [number, number, number] {
    const value: number = Number.parseInt(hex.replace('#', ''), 16);
    return [Math.floor(value / 65536) % 256, Math.floor(value / 256) % 256, value % 256];
}

/**
 * RGBA texels of one tile of `colorHex` wood: every channel of the species colour is scaled by
 * `1 + WOOD_GRAIN_AMPLITUDE × grain`, so the hue stays and the luminance moves inside the band.
 */
export function woodAlbedoPixels(colorHex: string, size: number, seed: number): Uint8ClampedArray {
    const [red, green, blue]: [number, number, number] = parseHexColor(colorHex);
    const field: Float32Array = woodGrainField(size, seed);
    const pixels: Uint8ClampedArray = new Uint8ClampedArray(size * size * 4);
    for (let i: number = 0; i < field.length; i++) {
        const scale: number = 1 + WOOD_GRAIN_AMPLITUDE * field[i];
        pixels[i * 4] = Math.round(red * scale);
        pixels[i * 4 + 1] = Math.round(green * scale);
        pixels[i * 4 + 2] = Math.round(blue * scale);
        pixels[i * 4 + 3] = 255;
    }
    return pixels;
}

/**
 * The wood texture of one species, drawn once on a canvas of `doc`. Returns `null` where there is
 * no document or no 2D canvas (prerender, unit tests): the material then keeps the plain colour.
 */
export function createWoodTexture(doc: Document | undefined, colorHex: string, seedKey: string): CanvasTexture | null {
    if (!doc) {
        return null;
    }
    try {
        const canvas: HTMLCanvasElement = doc.createElement('canvas');
        canvas.width = WOOD_TEXTURE_SIZE;
        canvas.height = WOOD_TEXTURE_SIZE;
        const context: CanvasRenderingContext2D | null = canvas.getContext('2d') ?? null;
        if (!context) {
            return null;
        }
        const image: ImageData = context.createImageData(WOOD_TEXTURE_SIZE, WOOD_TEXTURE_SIZE);
        image.data.set(woodAlbedoPixels(colorHex, WOOD_TEXTURE_SIZE, hashString(seedKey)));
        context.putImageData(image, 0, 0);

        const texture: CanvasTexture = new CanvasTexture(canvas);
        texture.wrapS = RepeatWrapping;
        texture.wrapT = RepeatWrapping;
        texture.colorSpace = SRGBColorSpace;
        texture.anisotropy = 8;
        texture.generateMipmaps = true;
        texture.minFilter = LinearMipmapLinearFilter;
        return texture;
    } catch {
        return null;
    }
}

/**
 * Rewrites `BoxGeometry` per-face UVs so that the texture tiles at world scale (one tile =
 * `WOOD_TILE_MM`) and its U axis — the grain — runs along `grainAxis` on every face where that
 * axis lies in the face plane. End-grain faces keep the same scale. `offset` shifts the tile so
 * neighbouring boards of one size do not repeat the same pattern.
 */
export function applyBoxWoodUV(geometry: BoxGeometry, size: [number, number, number], grainAxis: IGrainAxis, offset: number = 0): void {
    const uvAttr: BufferAttribute = geometry.attributes['uv'] as BufferAttribute;
    const [wU, hU, dU]: number[] = size.map((side: number) => side / WOOD_TILE_MM);

    // Default BoxGeometry UV layout per face: ±X (u ← Z, v ← Y), ±Y (u ← X, v ← Z), ±Z (u ← X, v ← Y).
    const faces: { vAxis: IGrainAxis; uSize: number; vSize: number }[] = [
        { vAxis: 'y', uSize: dU, vSize: hU },
        { vAxis: 'y', uSize: dU, vSize: hU },
        { vAxis: 'z', uSize: wU, vSize: dU },
        { vAxis: 'z', uSize: wU, vSize: dU },
        { vAxis: 'y', uSize: wU, vSize: hU },
        { vAxis: 'y', uSize: wU, vSize: hU },
    ];

    faces.forEach((face: { vAxis: IGrainAxis; uSize: number; vSize: number }, index: number) => {
        const swap: boolean = grainAxis === face.vAxis;
        for (let corner: number = 0; corner < 4; corner++) {
            const vertex: number = index * 4 + corner;
            const u: number = uvAttr.getX(vertex) * face.uSize;
            const v: number = uvAttr.getY(vertex) * face.vSize;
            uvAttr.setXY(vertex, (swap ? v : u) + offset, (swap ? u : v) + offset);
        }
    });
    uvAttr.needsUpdate = true;
}

/**
 * Brings `ExtrudeGeometry` UVs (shape millimetres) to world scale. On the two side faces of the
 * board (group 0) the grain turns by `grainAngle` — the slope of the board in the shape plane —
 * so it runs along the stringer, not along the floor.
 */
export function applyExtrudeWoodUV(geometry: BufferGeometry, grainAngle: number): void {
    const uvAttr: BufferAttribute = geometry.attributes['uv'] as BufferAttribute;
    const capEnd: number = geometry.groups.length > 1 ? geometry.groups[1].start : uvAttr.count;
    const cos: number = Math.cos(grainAngle);
    const sin: number = Math.sin(grainAngle);
    for (let i: number = 0; i < uvAttr.count; i++) {
        const u: number = uvAttr.getX(i);
        const v: number = uvAttr.getY(i);
        if (i < capEnd) {
            uvAttr.setXY(i, (u * cos + v * sin) / WOOD_TILE_MM, (v * cos - u * sin) / WOOD_TILE_MM);
        } else {
            uvAttr.setXY(i, u / WOOD_TILE_MM, v / WOOD_TILE_MM);
        }
    }
    uvAttr.needsUpdate = true;
}
